import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDay, humanize } from "../lib/format";
import { Badge, Checkbox, Empty, Segment } from "../ui";

export const KIND_LABEL = {
    linkedin_connections: "LinkedIn connections",
    linkedin_invitations: "LinkedIn invitations",
    linkedin_messages: "LinkedIn messages",
    tracker: "Tracker workbook",
};

const ACTION_COLOR = { created: "success", updated: "info", unchanged: "secondary", skipped: "warning", error: "danger" };

export function ReportCounts({ report }) {
    const items = [
        ["created", "Created"],
        ["updated", "Updated"],
        ["unchanged", "Unchanged"],
        ["skipped", "Skipped"],
        ["errors", "Errors"],
        ["possible_duplicates", "Possible duplicates"],
        ["role_changes", "Role changes"],
        ["skipped_manual_role", "Kept manual role"],
    ];
    return (
        <div className="hz-counts">
            {items.map(([k, label]) => (
                <div key={k}>
                    <div className="hz-stat" style={{ color: k === "errors" && report[k] ? "var(--hz-danger-text)" : undefined }}>{report[k] || 0}</div>
                    <div className="hz-stat-label">{label}</div>
                </div>
            ))}
        </div>
    );
}

const FILTERS = [
    { value: "all", label: "All" },
    { value: "created", label: "Created" },
    { value: "updated", label: "Updated" },
    { value: "role", label: "Role changes" },
    { value: "dupes", label: "Possible duplicates" },
    { value: "skipped", label: "Skipped" },
    { value: "error", label: "Errors" },
];

const matches = (row, f) => {
    if (f === "all") return true;
    if (f === "role") return !!row.role_change || row.skipped_manual_role;
    if (f === "dupes") return row.possible_duplicates && row.possible_duplicates.length > 0;
    return row.action === f;
};

function RowDetail({ row }) {
    const parts = [];
    if (row.role_change) {
        const rc = row.role_change;
        parts.push(
            <div key="rc">
                <i className="ri-briefcase-4-line"></i> {rc.from_title || "?"} at {rc.from_company || "?"} <i className="ri-arrow-right-line hz-muted"></i> {rc.to_title || "?"} at {rc.to_company || "?"}
            </div>
        );
    }
    if (row.skipped_manual_role) parts.push(<div key="mr" className="hz-muted">Kept your manual role.</div>);
    if (row.fields && row.fields.length) parts.push(<div key="f" className="hz-muted">Changes: {row.fields.map(humanize).join(", ")}</div>);
    if (row.last_messaged_at) parts.push(<div key="lm" className="hz-muted">Last messaged {formatDay(row.last_messaged_at)} · {row.conversations || 0} conversations</div>);
    if (row.reason && !(row.skipped_manual_role && row.reason === "kept your manual role")) parts.push(<div key="r" className={row.action === "error" ? "hz-error-text" : "hz-muted"}>{row.reason}</div>);
    if (row.possible_duplicates && row.possible_duplicates.length) {
        parts.push(
            <div key="pd" style={{ color: "var(--hz-warning-text)" }}>
                Possible duplicate of{" "}
                {row.possible_duplicates.map((d, i) => (
                    <React.Fragment key={d.id}>
                        {i > 0 && ", "}
                        <Link to={`/crm/contacts/${d.id}`}>{d.name}</Link>
                    </React.Fragment>
                ))}
            </div>
        );
    }
    if (row.matched_by) parts.push(<div key="m" className="hz-xs hz-muted">Matched by {humanize(row.matched_by)}</div>);
    return <div className="hz-small" style={{ display: "grid", gap: 2 }}>{parts}</div>;
}

// Rows of an import report. With selectable, rows that would write something
// get a checkbox; selection is a Set of row keys.
export function ReportRows({ report, selectable, selected, onSelect }) {
    const [filter, setFilter] = useState("all");
    const rows = useMemo(() => (report.rows || []).filter((r) => matches(r, filter)), [report, filter]);
    const canPick = (r) => selectable && (r.action === "created" || r.action === "updated");
    const pickable = rows.filter(canPick);
    const allPicked = pickable.length > 0 && pickable.every((r) => selected.has(r.key));
    const toggleAll = (on) => {
        const next = new Set(selected);
        pickable.forEach((r) => (on ? next.add(r.key) : next.delete(r.key)));
        onSelect(next);
    };
    const toggle = (key, on) => {
        const next = new Set(selected);
        if (on) next.add(key);
        else next.delete(key);
        onSelect(next);
    };
    const entityLink = (r) => {
        if (!r.entity_id || report.dry_run) return r.name || r.key;
        const base = r.entity_type === "company" ? "/crm/companies/" : r.entity_type === "contact" ? "/crm/contacts/" : null;
        return base ? <Link to={base + r.entity_id}>{r.name || r.key}</Link> : r.name || r.key;
    };
    return (
        <>
            <div className="hz-filterbar">
                <Segment options={FILTERS} value={filter} onChange={setFilter} />
                <span className="hz-spacer"></span>
                {selectable && pickable.length > 0 && (
                    <Checkbox checked={allPicked} onChange={toggleAll}>Select all shown</Checkbox>
                )}
            </div>
            <div className="hz-table-wrap" style={{ maxHeight: 520, overflowY: "auto" }}>
                <table className="hz-table">
                    <thead>
                        <tr>
                            {selectable && <th style={{ width: 32 }}></th>}
                            <th>Row</th>
                            <th>Name</th>
                            <th>Action</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key + r.row + (r.tab || "")}>
                                {selectable && (
                                    <td>{canPick(r) && <input type="checkbox" checked={selected.has(r.key)} onChange={(e) => toggle(r.key, e.target.checked)} aria-label={`Select ${r.name || r.key}`} />}</td>
                                )}
                                <td className="hz-small hz-muted hz-nowrap">{r.tab ? `${r.tab} ` : ""}{r.row}</td>
                                <td>{entityLink(r)}</td>
                                <td><Badge color={ACTION_COLOR[r.action] || "secondary"}>{r.action}</Badge></td>
                                <td><RowDetail row={r} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 && <Empty>{(report.rows || []).length === 0 ? "Every row is unchanged." : "No rows match this filter."}</Empty>}
            </div>
        </>
    );
}
